import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api } from './api'
import { authConfigured, supabase } from './supabase'

/**
 * Who the caller is, as the SERVER reports them.
 *
 * The client never decides this. Heimar.Api validates the Supabase access token
 * and reads the account's own row, so what lands here is already authoritative;
 * it is used to choose what to render, and nothing more.
 *
 *   anonymous — no account. Sees the way in.
 *   member    — signed in. May browse the shelf and read any pack page.
 *   approved  — may play. The gate is on PLAYTHROUGHS, not on accounts: the
 *               engine is what costs money, so it is the only thing a person
 *               has to be let in for.
 *
 * These mirror Heimar.Api/Services/AccessControl.cs `AccessTier`. They change
 * only when it does.
 */
export type AccountStatus = 'anonymous' | 'member' | 'approved'

/** The doors Supabase is configured to open. */
export type OAuthProvider = 'google' | 'discord' | 'apple'

export interface Account {
  status: AccountStatus
  email: string | null
  isAdmin: boolean
  /** True when no Supabase project is configured — local development only. */
  open: boolean
}

interface AuthState extends Account {
  loading: boolean
  /** Sends a magic link. Signing up and signing in are the same act. */
  signIn: (email: string) => Promise<{ ok: boolean; error?: string }>
  /** Hands off to a provider. Navigates away; nothing after it runs on success. */
  signInWith: (provider: OAuthProvider) => Promise<{ ok: boolean; error?: string }>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

const ANONYMOUS: Account = {
  status: 'anonymous',
  email: null,
  isAdmin: false,
  open: false,
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<Account>(ANONYMOUS)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      setAccount(await api.me())
    } catch {
      setAccount(ANONYMOUS)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
    if (!supabase) return
    // Re-ask the server whenever the token changes — including the magic-link
    // callback, which signs the user in on page load.
    const { data } = supabase.auth.onAuthStateChange(() => {
      void refresh()
    })
    return () => data.subscription.unsubscribe()
  }, [refresh])

  const signIn = useCallback(async (email: string) => {
    if (!supabase) return { ok: false, error: 'Sign-in is not configured.' }
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    })
    return error ? { ok: false, error: error.message } : { ok: true }
  }, [])

  const signInWith = useCallback(async (provider: OAuthProvider) => {
    if (!supabase) return { ok: false, error: 'Sign-in is not configured.' }

    // On success the browser LEAVES for the provider, so nothing after this
    // resolves in the current document. The returned error only ever describes a
    // failure to start the hand-off (a provider that is not enabled in Supabase,
    // usually) — which is why the caller must not treat "no error" as "signed in".
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin },
    })
    return error ? { ok: false, error: error.message } : { ok: true }
  }, [])

  const signOut = useCallback(async () => {
    await supabase?.auth.signOut()
    setAccount(ANONYMOUS)
  }, [])

  const value = useMemo<AuthState>(
    () => ({ ...account, loading, signIn, signInWith, signOut, refresh }),
    [account, loading, signIn, signInWith, signOut, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}

export { authConfigured }
